import { type RefObject, useEffect } from 'react';
import { type ShortcutContext, resolveShortcut } from './shortcut-map';
import { isTypingTarget } from './use-immersive';

/**
 * Skróty klawiszowe odtwarzacza.
 *
 * Sama decyzja „co robi ten klawisz" mieszka w `resolveShortcut` — czystej
 * funkcji pokrytej testami. Tutaj zostaje wyłącznie wykonanie: nasłuch,
 * odrzucenie zdarzeń z pól tekstowych i sięgnięcie do elementu wideo.
 *
 * Nasłuch jest na OKNIE, nie na elemencie wideo: element traci fokus przy
 * pierwszym kliknięciu w cokolwiek innego, a widz oczekuje, że spacja działa
 * zawsze.
 */

export interface ShortcutActions {
  onToggleFullscreen(): void;
  onToggleSubtitles(): void;
  /** Podane tylko wtedy, gdy jest co pominąć. */
  onSkip?: (() => void) | undefined;
  onNextEpisode?: (() => void) | undefined;
  /** Przesunięcie napisów; brak oznacza, że nie ma czego przesuwać. */
  onSubtitleShift?: ((deltaSec: number) => void) | undefined;
}

export function usePlayerShortcuts({
  videoRef,
  enabled,
  canControlPlayback = true,
  hasActiveSubtitles = false,
  actions,
}: {
  videoRef: RefObject<HTMLVideoElement | null>;
  enabled: boolean;
  canControlPlayback?: boolean;
  hasActiveSubtitles?: boolean;
  actions: ShortcutActions;
}): void {
  useEffect(() => {
    if (!enabled) return;

    function onKey(event: KeyboardEvent): void {
      // Bez tego spacja wpisana w czacie pokoju zatrzymywałaby odcinek
      // wszystkim, a `f` wrzucało na pełny ekran w środku zdania.
      if (isTypingTarget(event.target)) return;
      // Kombinacje z modyfikatorami należą do przeglądarki i systemu.
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      const video = videoRef.current;
      if (video === null) return;

      const ctx: ShortcutContext = {
        shiftKey: event.shiftKey,
        canControlPlayback,
        hasSkip: actions.onSkip !== undefined,
        hasNextEpisode: actions.onNextEpisode !== undefined,
        hasActiveSubtitles,
      };

      const action = resolveShortcut(event.key, ctx);
      if (action.kind === 'none') return;
      event.preventDefault();

      switch (action.kind) {
        case 'fullscreen':
          actions.onToggleFullscreen();
          break;
        case 'subtitles':
          actions.onToggleSubtitles();
          break;
        case 'mute':
          video.muted = !video.muted;
          break;
        case 'volume':
          video.volume = Math.max(0, Math.min(1, video.volume + action.delta));
          break;
        case 'playPause':
          if (video.paused) void video.play().catch(() => undefined);
          else video.pause();
          break;
        case 'seek':
          // Górnej granicy nie narzucamy: przeglądarka przycina do długości,
          // a `duration` bywa `NaN`, dopóki metadane się nie wczytają.
          video.currentTime = Math.max(0, video.currentTime + action.deltaSec);
          break;
        case 'skip':
          actions.onSkip?.();
          break;
        case 'nextEpisode':
          actions.onNextEpisode?.();
          break;
        case 'subtitleShift':
          actions.onSubtitleShift?.(action.deltaSec);
          break;
      }
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, canControlPlayback, hasActiveSubtitles, videoRef, actions]);
}
